import { isSyncableKey } from "./syncMerge.js";

/**
 * Popup « nouveautés », listant `app/data/whats-new.json` (la plus récente en
 * premier). Ouverte par tout élément `[data-open-news]` — aujourd'hui l'entrée
 * du menu du compte. Pastille tant que la dernière entrée n'a pas été vue ; la
 * popup s'ouvre aussi automatiquement une fois par utilisateur tant que la
 * dernière entrée n'a pas été vue (donc à nouveau au prochain ajout dans
 * whats-new.json) — c'est le seul canal pour qui n'a pas de compte, puisque
 * l'entrée de menu vit dans le menu du compte.
 *
 * « Vue » est mémorisé globalement, PAS préfixé par langue (comme
 * `geonerd.muted` dans SoundManager.js) : lue en français, la pastille ne
 * doit pas réapparaître en passant en anglais.
 */
const STORAGE_KEY = "geonerd.news.lastSeen";
const DATA_URL = "/data/whats-new.json";

/**
 * Le visiteur a-t-il déjà une trace de jeu ?
 *
 * `isSyncableKey` est la définition centrale de « donnée de joueur qui compte »
 * (records, stats cumulées, défis du jour, progression Country Nerd, carte
 * explorée) : la réutiliser évite d'en tenir une seconde liste ici. S'y ajoute
 * le chrono d'une partie commencée mais jamais finie, qui ne laisse aucune de
 * ces clés derrière lui.
 */
function hasPlayedBefore() {
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (isSyncableKey(key) || key.endsWith(".startedAt")) return true;
    }
    return false;
}

export default class NotificationsUI {
    constructor() {
        this.triggers = document.querySelectorAll("[data-open-news]");
        this.popup = document.querySelector(".notifications-popup");
        // Pas de `return` sur l'absence de déclencheur : un visiteur non connecté
        // n'en a aucun, mais l'ouverture automatique doit quand même avoir lieu.
        if (!this.popup) return;

        this.entries = [];
        this.bindTriggers();
        this.bindClose();
        this.load();
    }

    async load() {
        try {
            const res = await fetch(DATA_URL);
            this.entries = res.ok ? await res.json() : [];
        } catch {
            this.entries = [];
        }
        this.render();
        this.refreshDot();
        this.autoOpenIfUnseen();
    }

    autoOpenIfUnseen() {
        // Ne pas interrompre une partie en cours (timer de SpeedRun, etc.).
        if (document.body.classList.contains("is-gaming")) return;
        const latest = this.entries[0]?.id;
        if (!latest) return;

        const seen = localStorage.getItem(STORAGE_KEY);
        if (latest === seen) return;

        // Première visite : un changelog ne parle qu'à qui connaissait le jeu
        // d'avant. Le nouveau venu recevait « Country Nerd entre au classement »
        // en pleine figure avant même d'avoir vu la page d'accueil. Ce qui est
        // déjà en ligne fait partie du jeu tel qu'il le découvre : on le marque
        // lu sans rien ouvrir, il verra les nouveautés suivantes, celles qui
        // changeront vraiment quelque chose pour lui.
        if (!seen && !hasPlayedBefore()) {
            this.markSeen();
            return;
        }

        this.open();
    }

    /** Enregistre la dernière entrée comme lue et éteint la pastille. */
    markSeen() {
        const latest = this.entries[0]?.id;
        if (!latest) return;
        localStorage.setItem(STORAGE_KEY, latest);
        this.triggers.forEach((el) => el.classList.remove("has-unread"));
    }

    bindTriggers() {
        this.triggers.forEach((el) => el.addEventListener("click", () => this.open()));
    }

    bindClose() {
        this.popup.querySelectorAll("[data-notifications-close]").forEach((el) =>
            el.addEventListener("click", () => this.close())
        );
        // Clic sur l'overlay (hors contenu) ferme. Un bouton « OK, compris »
        // d'une entrée (`data-notifications-ok` dans whats-new.json, rendu après
        // coup, d'où la délégation) ferme aussi, et ramène à l'accueil si la
        // popup s'est ouverte ailleurs : elle s'ouvre seule sur n'importe quelle page.
        this.popup.addEventListener("click", (e) => {
            if (e.target === this.popup) {
                this.close();
                return;
            }
            if (e.target.closest("[data-notifications-ok]")) {
                this.close();
                if (!document.querySelector(".page.home")) this.goHome();
            }
        });
    }

    goHome() {
        window.location.assign(window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/");
    }

    render() {
        const list = this.popup.querySelector(".notifications-list");
        if (!list) return;
        const lang = window.geoNerdApp.languageManager.currentLang;
        list.innerHTML = this.entries.map((entry) => {
            const content = entry[lang] || entry.fr;
            return `<div class="notification-item">
                <span class="notification-date">${content.date}</span>
                ${content.html}
            </div>`;
        }).join("");
    }

    refreshDot() {
        const latest = this.entries[0]?.id;
        const seen = localStorage.getItem(STORAGE_KEY);
        const unread = !!latest && latest !== seen;
        this.triggers.forEach((el) => el.classList.toggle("has-unread", unread));
    }

    open() {
        this.popup.classList.add("show");
        this.markSeen();
    }

    close() {
        this.popup.classList.remove("show");
    }
}
